import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Layout from "../../Layout/Layout";
import Constant from "../Constant";
import {
  createStory,
  createStoryDraft,
  updateStoryDraft,
  getStoryDraftById,
  getStoryCategoriesCopy,
  getLanguages,
  uploadThumbImage,
  uploadIDCardsImage,
  createPaymentRequest,
  getContestDataById,
} from "../utilities/ApiCalls";
import { getTokenDetails } from "../utilities/CommonFunction";
import WriteContent from "./WriteContent";

/**
 * Wrapper for all story writes (regular + contest).
 * Initial version wires regular submissions; contest / drafts will be
 * expanded iteratively.
 */

function WriteStory(props) {
  const history = useHistory();
  const location = useLocation();

  const query = new URLSearchParams(props.location?.search || location.search);
  const contestId = query.get("contestId") || query.get("contest") || "";
  const draftId = query.get("id");
  const isDraftParam = query.get("isDraft") === "1";
  const stepParam = query.get("step");
  const initialPage = stepParam === "setup" ? 2 : 1;
  const amountParam = query.get("amount");
  const amount = amountParam ? parseInt(amountParam, 10) : null;

  const [contestData, setContestData] = useState(null);
  const [initialForm, setInitialForm] = useState({
    contestId: contestId || "",
  });

  const isContest = !!(contestId || initialForm.contestId);

  const apis = useMemo(
    () => ({
      createContent: createStory,
      createDraft: createStoryDraft,
      updateDraft: updateStoryDraft,
      getDrafts: null, // not used directly for resume
      getCategories: getStoryCategoriesCopy,
      getLanguages,
      uploadThumbImage,
      uploadIDCardsImage,
      createPaymentRequest,
      getContestDataById,
    }),
    []
  );

  const navigation = useMemo(
    () => ({
      onDraftSaved: ({ isContest: draftIsContest }) => {
        if (draftIsContest) {
          setTimeout(() => {
            history.push("/events");
          }, 1500);
        } else {
          setTimeout(() => {
            history.push("/create");
          }, 1500);
        }
      },
      onSubmitSuccess: ({ isContest: submitIsContest, response }) => {
        const token = localStorage.getItem("loginDetails");
        const userDetails = token ? getTokenDetails(token) : null;

        if (submitIsContest && amount && response?.data?._id) {
          const paymentRequest = {
            amount: amount,
            purpose: "Write story",
            redirectURL: `${Constant.domain}profile/${userDetails?._id || ""}`,
            isPrime: 1,
            paymentFor: 2,
            enrollmentId: response.data._id,
          };

          createPaymentRequest(paymentRequest)
            .then((orderResponse) => {
              if (orderResponse.status === Constant.statusSuccess) {
                setTimeout(() => {
                  window.location.href = orderResponse.data.longurl;
                }, 1500);
              } else {
                setTimeout(() => {
                  history.push("/events");
                }, 1500);
              }
            })
            .catch(() => {
              setTimeout(() => {
                history.push("/events");
              }, 1500);
            });
          return;
        }

        // After successful submission without payment:
        // - Contest: go to /events
        // - Regular: go to /create
        if (submitIsContest) {
          setTimeout(() => {
            history.push("/events");
          }, 1500);
        } else {
          setTimeout(() => {
            history.push({
              pathname: "/",
              state: { newPost: { ...response?.data, type: "story" } }
            });
          }, 1500);
        }
      },
    }),
    [amount, history]
  );

  // Load draft by id when coming with ?id=...&isDraft=1
  useEffect(() => {
    let cancelled = false;

    const loadDraft = async () => {
      if (!draftId || !isDraftParam) return;
      console.log("[WriteStory] Loading draft by id:", draftId);
      try {
        const resp = await getStoryDraftById(draftId);
        if (cancelled) return;
        if (resp.status !== Constant.statusFailure && resp.data) {
          const draft = resp.data;
          console.log("[WriteStory] Draft loaded:", draft);
          setInitialForm({
            id: draft._id,
            name: draft.name || "",
            description: draft.description || "",
            thumbnail: draft.thumbnail || "",
            duration: draft.duration || "",
            categoryId: draft.categoryId || [],
            language: draft.language || "",
            contestId: draft.contestId || contestId || "",
            isAdultContent: !!draft.isAdultContent,
          });
        }
      } catch {
        // ignore; keep defaults
      }
    };

    loadDraft();

    return () => {
      cancelled = true;
    };
  }, [draftId, isDraftParam, contestId]);

  // Fetch contest data when contestId is present
  useEffect(() => {
    let cancelled = false;

    const fetchContest = async () => {
      const effectiveContestId = contestId || initialForm.contestId;
      if (!effectiveContestId) return;
      try {
        const resp = await getContestDataById({ id: effectiveContestId });
        if (cancelled) return;
        if (resp.status !== Constant.statusFailure && Array.isArray(resp.data)) {
          setContestData(resp.data[0] || null);
        } else {
          setContestData(null);
        }
      } catch {
        if (cancelled) return;
        setContestData(null);
      }
    };

    fetchContest();

    return () => {
      cancelled = true;
    };
  }, [contestId, initialForm.contestId]);

  return (
    <Layout>
      <WriteContent
        mode="story"
        isContest={isContest}
        initialForm={initialForm}
        initialPage={initialPage}
        contestData={contestData}
        amount={amount}
        apis={apis}
        navigation={navigation}
      />
    </Layout>
  );
}

export default WriteStory;

